import React, { useState, useRef, useCallback, useEffect } from 'react';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import PerspectiveAssigneesDisplayAtom from '../../atoms/PerspectiveAssigneesDisplay/PerspectiveAssigneesDisplayAtom';
import { Sphere } from 'types/global';
import type { GlyphColor } from '../../../types/glyph';
import { useTranslation } from 'localization';
import './AssigneeMultiSelectorMolecule.scss';

interface Assignee {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
}

interface AssigneeMultiSelectorProps {
  options: Assignee[];
  selectedAssignees: Assignee[];
  onSelectionChange: (selected: Assignee[]) => void;
  onDone?: () => void;
  isEditing: boolean;
  placeholder?: string;
  showNearbyToggle?: boolean;
  onNearbyModeToggled?: (nearbyMode: boolean) => void;
  /** Callback for server-side search - if provided, disables client-side filtering */
  onSearchChange?: (searchTerm: string) => void;
  /** Callback to load more results */
  onLoadMore?: () => void;
  /** Whether more results are available */
  hasMore?: boolean;
  /** Whether currently loading more results */
  isLoadingMore?: boolean;
  /** Controlled search value (optional - for lifting state to parent) */
  searchValue?: string;
}

const AssigneeMultiSelectorMolecule: React.FC<AssigneeMultiSelectorProps> = React.memo(({
  options,
  selectedAssignees,
  onSelectionChange,
  onDone,
  isEditing,
  placeholder,
  showNearbyToggle = false,
  onNearbyModeToggled,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  searchValue,
}) => {
  const { t } = useTranslation();
  // Use controlled value if provided, otherwise use local state
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const searchQuery = searchValue === undefined ? localSearchQuery : searchValue;

  const [nearbyMode, setNearbyMode] = useState(false);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(isLoadingMore);

  // Keep ref in sync with prop
  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Handle search input change with optional server-side search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Update local state if not controlled
    if (searchValue === undefined) {
      setLocalSearchQuery(value);
    }
    // If server-side search is enabled, call the callback
    if (onSearchChange) {
      onSearchChange(value);
    }
  }, [onSearchChange, searchValue]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!resultsListRef.current || !onLoadMore || !hasMore || isLoadingRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = resultsListRef.current;
    const scrollThreshold = 50; // pixels from bottom to trigger load

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore]);

  // If server-side search is enabled, don't filter client-side
  // Otherwise, filter options based on search query
  const filteredOptions = onSearchChange
    ? options
    : options.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const selectedCodes = new Set(selectedAssignees.map((a) => a.code));
  const unselectedOptions = filteredOptions.filter((opt) => !selectedCodes.has(opt.code));

  const handleToggleSelection = (assignee: Assignee) => {
    const isSelected = selectedCodes.has(assignee.code);
    if (isSelected) {
      onSelectionChange(selectedAssignees.filter((a) => a.code !== assignee.code));
    } else {
      onSelectionChange([...selectedAssignees, assignee]);
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleNearbyToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newMode = !nearbyMode;
    setNearbyMode(newMode);
    onNearbyModeToggled?.(newMode);
  };

  if (!isEditing) {
    if (selectedAssignees.length === 0) {
      return (
        <div className="assignee-selector-empty">
          <span className="empty-state">—</span>
        </div>
      );
    }

    const spheres: Sphere[] = selectedAssignees.map((a) => ({
      id: a.code,
      label: 'subject',
      glyph: a.glyph,
      glyphColor: a.glyphColor,
      properties: {
        name: a.name,
        imageId: a.imageId,
        type: 'person',
      },
    } as Sphere));

    return (
      <div className="assignee-selector-readonly">
        <PerspectiveAssigneesDisplayAtom assignees={spheres} size="small" />
      </div>
    );
  }

  return (
    <div className="assignee-multi-selector">
      <div className="selector-header">
        <span className="header-title">{t('assigneeMultiSelector.title')}</span>
        {showNearbyToggle && (
          <div className="nearby-toggle">
            <span className="toggle-label">{t('assigneeMultiSelector.nearby')}</span>
            <button
              className={`toggle-button ${nearbyMode ? 'active' : ''}`}
              onClick={handleNearbyToggle}
              aria-label={t('assigneeMultiSelector.toggleNearby')}
              aria-pressed={nearbyMode}
              type="button"
            >
              <span className="toggle-slider" />
            </button>
          </div>
        )}
      </div>

      <div className="search-box">
        <input
          type="text"
          placeholder={placeholder ?? t('assigneeMultiSelector.searchPlaceholder')}
          aria-label={t('assigneeMultiSelector.searchLabel')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-input"
        />
        {searchQuery && (
          <button
            type="button"
            className="search-clear-button"
            onClick={(e) => {
              e.stopPropagation();
              if (searchValue === undefined) {
                setLocalSearchQuery('');
              }
              if (onSearchChange) {
                onSearchChange('');
              }
            }}
            aria-label={t('assigneeMultiSelector.clearSearch')}
          >
            ×
          </button>
        )}
      </div>

      {selectedAssignees.length > 0 && (
        <div className="selected-section">
          <div className="section-header">{t('assigneeMultiSelector.selected', { count: selectedAssignees.length })}</div>
          <div className="assignee-list">
            {selectedAssignees.map((assignee) => (
              <button
                key={assignee.code}
                type="button"
                className="assignee-item selected"
                aria-pressed={true}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleSelection(assignee);
                }}
              >
                <span className="assignee-checkbox assignee-checkbox--checked" aria-hidden="true">
                  <i className="pi pi-check" />
                </span>
                <SphereAvatar
                  sphereId={assignee.code}
                  glyph={assignee.glyph}
                  glyphColor={assignee.glyphColor}
                  imageId={assignee.imageId}
                  decorative
                />
                <span className="assignee-name">{assignee.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {(unselectedOptions.length > 0 || isLoadingMore) && (
        <div className="results-section">
          <div className="section-header">
            {t('assigneeMultiSelector.results')}
            {isLoadingMore && <span className="loading-indicator">{t('assigneeMultiSelector.loadingMore')}</span>}
          </div>
          <div
            className="assignee-list"
            ref={resultsListRef}
            onScroll={handleScroll}
          >
            {unselectedOptions.map((assignee) => (
              <button
                key={assignee.code}
                type="button"
                className="assignee-item"
                aria-pressed={false}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleSelection(assignee);
                }}
              >
                <span className="assignee-checkbox" aria-hidden="true" />
                <SphereAvatar
                  sphereId={assignee.code}
                  glyph={assignee.glyph}
                  glyphColor={assignee.glyphColor}
                  imageId={assignee.imageId}
                  decorative
                />
                <span className="assignee-name">{assignee.name}</span>
              </button>
            ))}
            {hasMore && !isLoadingMore && (
              <div className="load-more-hint">{t('assigneeMultiSelector.scrollForMore')}</div>
            )}
          </div>
        </div>
      )}

      <div className="selector-footer">
        {selectedAssignees.length > 0 && (
          <button
            className="clear-all-button"
            onClick={(e) => {
              e.stopPropagation();
              handleClearAll();
            }}
            type="button"
          >
            {t('assigneeMultiSelector.clearAll')}
          </button>
        )}
        {onDone && (
          <button
            className="done-button"
            onClick={(e) => {
              e.stopPropagation();
              onDone();
            }}
            type="button"
          >
            {t('assigneeMultiSelector.done')}
          </button>
        )}
      </div>
    </div>
  );
});

AssigneeMultiSelectorMolecule.displayName = 'AssigneeMultiSelectorMolecule';

export default AssigneeMultiSelectorMolecule;
